import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { matangiKnowledge, type KnowledgeLang } from './matangi-knowledge';
import './matangi-knowledge.css';

export function MatangiKnowledgePage({lang}:{lang:KnowledgeLang}){
  const c=matangiKnowledge[lang];
  const [search,setSearch]=useState('');
  const q=search.trim().toLocaleLowerCase(lang==='ta'?'ta-IN':'en-US');
  const faqs=useMemo(()=>q?c.faqs.filter(x=>(x.q+' '+x.a).toLocaleLowerCase(lang==='ta'?'ta-IN':'en-US').includes(q)):c.faqs,[c,q,lang]);
  const glossary=useMemo(()=>q?c.glossary.filter(x=>(x.term+' '+x.definition).toLocaleLowerCase(lang==='ta'?'ta-IN':'en-US').includes(q)):c.glossary,[c,q,lang]);
  const siteQ=lang==='ta'?'?lang=ta':'';
  return <>
    <section className="mk-hero" aria-labelledby="mk-title">
      <div className="mk-hero-copy">
        <p className="mk-kicker">{c.hero.kicker}</p>
        <h1 id="mk-title"><span>{c.hero.titleTop}</span><em>{c.hero.titleAccent}</em></h1>
        <p className="mk-hero-lede">{c.hero.lede}</p>
        <div className="mk-hero-actions">
          <a className="mk-btn mk-btn-gold" href="#identity">{c.hero.primary}<span>↗</span></a>
          <a className="mk-text-link" href="#sacred-texts">{c.hero.secondary}<span>→</span></a>
        </div>
        <div className="mk-tags" aria-label={lang==='ta'?'தொடர்புடைய பெயர்கள்':'Related names'}>
          {c.hero.tags.map(x=><span key={x}>{x}</span>)}
        </div>
      </div>
      <div className="mk-hero-media">
        <img src="/media/04.webp" alt={lang==='ta'?'வீணையுடன் அலங்கரிக்கப்பட்ட ஸ்ரீ ராஜ மாதங்கி பீடத் தெய்வத் திருவுருவம்':'Adorned veena-bearing deity in the Sri Raja Matangi Peetam collection'} fetchPriority="high"/>
        <div className="mk-seal"><strong>श्री</strong><span>{c.hero.seal}</span></div>
        <figure className="mk-inset"><img src="/media/03.webp" alt={lang==='ta'?'ஸ்ரீ முத்து குமார் சிவாச்சாரியார் வழிபாடு செய்கிறார்':'Sri Muthu Kumar Sivachariyar conducting worship'}/></figure>
      </div>
    </section>

    <nav className="mk-chapter-nav" aria-label={lang==='ta'?'அத்தியாய வழிசெலுத்தல்':'Chapter navigation'}>
      {c.chapters.map(x=><a key={x.id} href={'#'+x.id}>{x.label}</a>)}
    </nav>

    <section className="mk-intro">
      <div>
        <p className="mk-kicker">{c.intro.eyebrow}</p>
        <h2>{c.intro.title}</h2>
      </div>
      <div>
        <p className="mk-large-copy">{c.intro.body}</p>
        <aside className="mk-note">{c.intro.note}</aside>
      </div>
    </section>

    <section className="mk-dimensions" aria-label={lang==='ta'?'நான்கு முக்கிய பரிமாணங்கள்':'Four essential dimensions'}>
      {c.dimensions.map(x=><article key={x.n}><span>{x.n}</span><h3>{x.title}</h3><p>{x.body}</p></article>)}
    </section>

    <section className="mk-section mk-identity" id="identity">
      <header className="mk-section-head">
        <p className="mk-kicker">{c.identity.eyebrow}</p>
        <h2>{c.identity.title}</h2>
        <p>{c.identity.body}</p>
      </header>
      <div className="mk-identity-grid">
        {c.identity.cards.map((x,i)=><article key={x.title} className={i===0?'featured':''}>
          <small>{x.status}</small><h3>{x.title}</h3><p>{x.body}</p>
        </article>)}
      </div>
    </section>

    <section className="mk-section mk-srividya" id="sri-vidya">
      <header className="mk-section-head light">
        <p className="mk-kicker">{c.sriVidya.eyebrow}</p>
        <h2>{c.sriVidya.title}</h2>
        <p>{c.sriVidya.body}</p>
      </header>
      <div className="mk-court" role="img" aria-label={lang==='ta'?'லலிதா, மந்திரிணி, தண்டநாதா ஆகியோரின் கருத்து வரைபடம்':'Conceptual map of Lalita, Mantrini and Dandanatha'}>
        <article className="mk-court-node side"><span>01</span><h3>{c.sriVidya.left}</h3><p>{c.sriVidya.leftBody}</p></article>
        <article className="mk-court-node center"><span>ॐ</span><h3>{c.sriVidya.center}</h3><p>{c.sriVidya.centerBody}</p></article>
        <article className="mk-court-node side"><span>03</span><h3>{c.sriVidya.right}</h3><p>{c.sriVidya.rightBody}</p></article>
      </div>
      <article className="mk-geya"><div className="mk-orbit" aria-hidden="true">◎</div><div><h3>{c.sriVidya.geyaTitle}</h3><p>{c.sriVidya.geyaBody}</p></div></article>
    </section>

    <section className="mk-section" id="sacred-texts">
      <header className="mk-section-head">
        <p className="mk-kicker">{c.texts.eyebrow}</p>
        <h2>{c.texts.title}</h2>
        <p>{c.texts.body}</p>
      </header>
      <div className="mk-evidence-grid">
        {c.sources.map(s=><article key={s.id}>
          <span>{s.id}</span><h3>{s.title}</h3><p className="mk-locator">{s.locator}</p>
          <h4>{lang==='ta'?'இது உறுதிப்படுத்துவது':'What it establishes'}</h4><p>{s.establishes}</p>
          <h4>{lang==='ta'?'ஆதாரத்தின் எல்லை':'Boundary'}</h4><p>{s.boundary}</p>
        </article>)}
      </div>
    </section>

    <section className="mk-name-garden">
      <div className="mk-section-head light">
        <p className="mk-kicker">{c.names.eyebrow}</p><h2>{c.names.title}</h2><p>{c.names.body}</p>
      </div>
      <div className="mk-name-grid">
        {c.names.items.map((x,i)=><article key={x.name}><span>{String(i+1).padStart(2,'0')}</span><h3>{x.name}</h3><small>{x.source}</small></article>)}
      </div>
    </section>

    <section className="mk-section" id="iconography">
      <header className="mk-section-head">
        <p className="mk-kicker">{c.iconography.eyebrow}</p><h2>{c.iconography.title}</h2><p>{c.iconography.body}</p>
      </header>
      <div className="mk-iconography">
        <div className="mk-iconography-image"><img src="/media/04.webp" alt={lang==='ta'?'ஸ்ரீ ராஜ மாதங்கி பீடத்தின் தெய்வத் திருவுருவம்':'Deity image from Sri Raja Matangi Peetam'}/></div>
        <div className="mk-iconography-list">
          {c.iconography.items.map((x,i)=><details key={x.title} open={i===0}>
            <summary><span className="mk-symbol">{x.symbol}</span><strong>{x.title}</strong><b>+</b></summary>
            <div><p>{x.body}</p><small>{lang==='ta'?'ஆதாரம்':'Source'} · {x.source}</small></div>
          </details>)}
        </div>
      </div>
    </section>

    <section className="mk-section mk-traditions" id="traditions">
      <header className="mk-section-head">
        <p className="mk-kicker">{c.traditions.eyebrow}</p><h2>{c.traditions.title}</h2><p>{c.traditions.body}</p>
      </header>
      <div className="mk-tradition-grid">
        {c.traditions.cards.map((x,i)=><article key={x.title}><span>{String(i+1).padStart(2,'0')}</span><h3>{x.title}</h3><p>{x.body}</p><aside>{x.boundary}</aside></article>)}
      </div>
    </section>

    <section className="mk-section mk-worship" id="worship">
      <div className="mk-worship-image"><img src="/media/05.webp" alt={lang==='ta'?'பீடத்தில் நடைபெறும் ஹோம வழிபாடு':'Homa worship at Sri Raja Matangi Peetam'}/></div>
      <div className="mk-worship-copy">
        <p className="mk-kicker">{c.worship.eyebrow}</p><h2>{c.worship.title}</h2><p>{c.worship.body}</p>
        <div className="mk-boundary-grid">
          <article><h3>{c.worship.publicTitle}</h3><ul>{c.worship.publicItems.map(x=><li key={x}>✓ <span>{x}</span></li>)}</ul></article>
          <article><h3>{c.worship.guidedTitle}</h3><ul>{c.worship.guidedItems.map(x=><li key={x}>◇ <span>{x}</span></li>)}</ul></article>
        </div>
        <Link className="mk-btn mk-btn-dark" to={`/worship${siteQ}`}>{c.worship.cta}<span>↗</span></Link>
      </div>
    </section>

    <section className="mk-contemplation">
      <p className="mk-kicker">{c.contemplation.eyebrow}</p><h2>{c.contemplation.title}</h2><p>{c.contemplation.body}</p>
    </section>

    <section className="mk-section mk-qa" id="questions">
      <header className="mk-section-head">
        <p className="mk-kicker">{lang==='ta'?'கேள்விகள் · சொற்களஞ்சியம்':'QUESTIONS · GLOSSARY'}</p>
        <h2>{c.faqTitle}</h2><p>{c.faqIntro}</p>
        <label className="mk-search"><span>{lang==='ta'?'தேடுங்கள்':'Search this guide'}</span><input value={search} onChange={e=>setSearch(e.target.value)} placeholder={lang==='ta'?'மந்திரிணி, கெயசக்ரம், வீணை…':'Mantrini, Geya Chakra, veena…'}/></label>
      </header>
      <div className="mk-qa-grid">
        <div className="mk-faq-list">
          {faqs.map((x,i)=><details key={x.q} open={!q&&i===0}><summary>{x.q}<span>+</span></summary><p>{x.a}</p></details>)}
          {faqs.length===0&&<p>{lang==='ta'?'பொருந்தும் கேள்வி இல்லை.':'No matching question.'}</p>}
        </div>
        <aside className="mk-glossary"><h3>{c.glossaryTitle}</h3>{glossary.map(x=><div key={x.term}><strong>{x.term}</strong><p>{x.definition}</p></div>)}</aside>
      </div>
    </section>

    <section className="mk-section mk-sources" id="sources">
      <header className="mk-section-head">
        <p className="mk-kicker">{lang==='ta'?'ஆதார வெளிப்படைத்தன்மை':'SOURCE TRANSPARENCY'}</p><h2>{c.sourcesTitle}</h2><p>{c.sourcesIntro}</p>
      </header>
      <div className="mk-source-register">
        {c.sources.map(s=><article key={s.id}><span>{s.id}</span><div><h3>{s.title}</h3><p className="mk-locator">{s.locator}</p><p><strong>{lang==='ta'?'உறுதிப்படுத்துவது':'Establishes'}:</strong> {s.establishes}</p><p><strong>{lang==='ta'?'எல்லை':'Boundary'}:</strong> {s.boundary}</p><a href={s.url} target="_blank" rel="noreferrer">{lang==='ta'?'மூலநூலைத் திறக்க':'Open source text'} ↗</a></div></article>)}
      </div>
    </section>

    <section className="mk-clusters">
      <div className="mk-section-head light"><p className="mk-kicker">{c.clusters.eyebrow}</p><h2>{c.clusters.title}</h2><p>{c.clusters.body}</p></div>
      <div className="mk-cluster-grid">{c.clusters.items.map(x=><Link key={x.title} to={`${x.href}${siteQ}`}><h3>{x.title}</h3><p>{x.body}</p><span>↗</span></Link>)}</div>
    </section>

    <section className="mk-close">
      <div><p className="mk-kicker">{c.close.eyebrow}</p><h2>{c.close.title}</h2><p>{c.close.body}</p><Link className="mk-btn mk-btn-gold" to={`/visit${siteQ}`}>{c.close.cta}<span>↗</span></Link></div>
      <img src="/media/06.webp" alt={lang==='ta'?'மலர் அலங்காரமிட்ட பீடச் சன்னதி':'Flower-adorned shrine at Sri Raja Matangi Peetam'}/>
    </section>

    <div className="mk-language-crosslink">
      <span>{lang==='ta'?'இந்த வழிகாட்டி ஆங்கிலத்திலும் கிடைக்கிறது.':'This guide is also available in Tamil.'}</span>
      <Link to={lang==='ta'?'/en/sri-raja-matangi/':'/ta/sri-raja-matangi/'}>{lang==='ta'?'Read in English':'தமிழில் படிக்க'} →</Link>
    </div>
  </>;
}
